import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import type { AppStartListening } from '@/app/listeners';
import type { TableView } from './tableViews';

/** The viewer's own views of data explorer tables, by object key (`stripe/invoices`). */
export interface TableViewsState {
  custom: Record<string, TableView[]>;
}

const STORAGE_KEY = 'oaa.kpi.views.data';
const NONE: TableView[] = [];

/** Stored views; storage access never throws (private mode, blocked storage). */
function loadTableViews(): TableViewsState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as Partial<TableViewsState>) : undefined;
    if (stored?.custom && typeof stored.custom === 'object') return { custom: stored.custom };
  } catch {
    // Unreadable or malformed: start without custom views.
  }
  return { custom: {} };
}

type KeyPayload<T = object> = PayloadAction<T & { key: string }>;

export const tableViewsSlice = createSlice({
  name: 'tableViews',
  initialState: loadTableViews,
  reducers: {
    tableViewCreated(state, { payload }: KeyPayload<{ view: TableView }>) {
      (state.custom[payload.key] ??= []).push(payload.view);
    },
    /** Replaces the filters and sort of a custom view. */
    tableViewSaved(state, { payload }: KeyPayload<{ view: TableView }>) {
      const views = state.custom[payload.key] ?? [];
      const index = views.findIndex((view) => view.id === payload.view.id);
      if (index >= 0) views[index] = payload.view;
    },
    tableViewRenamed(
      state,
      { payload }: KeyPayload<{ id: string; name: string; description: string }>,
    ) {
      const view = state.custom[payload.key]?.find((candidate) => candidate.id === payload.id);
      if (!view) return;
      view.name = payload.name;
      if (payload.description) view.description = payload.description;
      else delete view.description;
    },
    tableViewDeleted(state, { payload }: KeyPayload<{ id: string }>) {
      const views = state.custom[payload.key];
      if (views) state.custom[payload.key] = views.filter((view) => view.id !== payload.id);
    },
  },
  selectors: {
    selectCustomTableViews: (state, key: string) => state.custom[key] ?? NONE,
  },
});

export const { tableViewCreated, tableViewSaved, tableViewRenamed, tableViewDeleted } =
  tableViewsSlice.actions;
export const { selectCustomTableViews } = tableViewsSlice.selectors;

/** Persists custom table views whenever they change. */
export function registerAnalyticsListeners(startListening: AppStartListening) {
  startListening({
    matcher: isAnyOf(tableViewCreated, tableViewSaved, tableViewRenamed, tableViewDeleted),
    effect: (_action, api) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(api.getState().tableViews));
      } catch {
        // Storage unavailable: the views last for this page only.
      }
    },
  });
}
